/**
 * Text selection helper functions for validating text selections
 */

/**
 * Checks if a node is within an editable element
 * @param node - The node to check
 * @returns true if the node is within an editable element, false otherwise
 */
export const isWithinEditableElement = (node: Node): boolean => {
  let currentNode: Node | null = node;
  while (currentNode && currentNode !== document.body) {
    if (currentNode.nodeType === Node.ELEMENT_NODE) {
      const element = currentNode as Element;
      if (
        element.classList?.contains('ck-editor') ||
        element.classList?.contains('ck-content') ||
        element.getAttribute?.('contenteditable') === 'true' ||
        element.tagName === 'TEXTAREA' ||
        element.tagName === 'INPUT' ||
        element.classList?.contains('draggable-notepad')
      ) {
        return true;
      }
    }
    currentNode = currentNode.parentNode;
  }
  return false;
};

/**
 * Checks if a node is within a table element
 * @param node - The node to check
 * @returns true if the node is within a table element, false otherwise
 */
export const isWithinTableElement = (node: Node): boolean => {
  let currentNode: Node | null = node;
  while (currentNode && currentNode !== document.body) {
    if (currentNode.nodeType === Node.ELEMENT_NODE) {
      const element = currentNode as Element;
      if (
        element.tagName === 'TABLE' ||
        element.tagName === 'TR' ||
        element.tagName === 'TD' ||
        element.tagName === 'TH' ||
        element.tagName === 'THEAD' ||
        element.tagName === 'TBODY' ||
        element.tagName === 'TFOOT'
      ) {
        return true;
      }
    }
    currentNode = currentNode.parentNode;
  }
  return false;
};

/**
 * Gets valid containers for text selection
 * @returns Array of valid container elements
 */
export const getValidContainers = (): Element[] => {
  return [
    // Target show-html elements specifically within question text areas
    ...document.querySelectorAll('.question-text .show-html'),
    ...document.querySelectorAll('.show-fill-answer'),
    // Target all show-html elements outside question text areas
    // Note: We need both selectors because .question-text .show-html only matches
    // show-html elements nested inside .question-text, while .show-html matches
    // all show-html elements regardless of parent container
    ...document.querySelectorAll('.show-html'),
  ];
};

/**
 * Checks if a selection is within valid containers
 * @param selection - The selection object to check
 * @returns true if the selection is within valid containers, false otherwise
 */
export const isWithinValidContainer = (selection: Selection): boolean => {
  const containers = getValidContainers();

  // Check if selection is within any valid container
  for (const container of containers) {
    // Check if both anchor and focus nodes are within the same container
    //
    // anchorNode: The DOM node where the selection started (where user clicked/dragged from).
    //             This represents the starting point of the text selection.
    //
    // focusNode: The DOM node where the selection ended (where user released mouse/cursor).
    //            This represents the ending point of the text selection.
    //
    // Why check both:
    // - Text selections can span across multiple DOM nodes/elements
    // - When a user selects text, anchorNode and focusNode can be different nodes
    // - We need to ensure BOTH the start (anchorNode) AND end (focusNode) of the selection
    //   are within the same valid container to prevent invalid selections that span across
    //   different containers or invalid areas
    // - Using optional chaining (?.) and || null ensures we handle cases where selection
    //   might be null or nodes might be undefined
    if (
      container.contains(selection?.anchorNode || null) &&
      container.contains(selection?.focusNode || null)
    ) {
      return true;
    }
  }
  return false;
};
